/** Пункти лівого меню з розгорнутого меню типу запису (buildMenu): що показувати, назва розділу типу, параметри лічильника пункту. */
import type { IRecordListParams } from '@platform/core/types';
import { DEFAULT_MENU_ITEM } from './buildMenu';
import { RECORDS } from './menuTypeName';
import type { IMenuEntry } from './IMenuEntry';
import type { IMenuSection } from './IMenuSection';

/**
 * Пункт показується в лівому меню: подання, крім таблиці, без власного пресета чи фільтра (картки, дошка, календар, панель, зал засідань)
 * живуть у перемикачі подань над списком і на сторінках модуля — як у прототипі v4; пункт «За …» (етапи за полем) і пункт режиму бібліотеки
 * («Усі категорії →») — у меню. Чиста функція.
 */
export function isMenuEntry(entry: IMenuEntry): boolean {
  return !entry.view || entry.view === 'table' || !!entry.preset || !!entry.filter || !!entry.mode || entry.key.startsWith('facet:');
}

/** Розділи для лівого меню: лише пункти isMenuEntry; розділ без таких пунктів пропущено. */
export function visibleSections(sections: IMenuSection[]): IMenuSection[] {
  return sections.map((s) => ({ ...s, entries: s.entries.filter(isMenuEntry) })).filter((s) => s.entries.length > 0);
}

/**
 * Заголовок розділу типу запису: назва типу; тип, названий так само, як модуль («Верховна Рада»), — за пунктом «Усі …» без «Усі»/«All»
 * з великої літери («Усі мандати» → «Мандати»), бо заголовок повторював би назву модуля над ним; загальне «Усі записи» — назва типу.
 */
export function typeSectionTitle(typeName: string, moduleName: string, entries: IMenuEntry[]): string {
  if (typeName.toLocaleLowerCase() !== moduleName.toLocaleLowerCase()) return typeName;
  const all = entries.find((e) => e.key === DEFAULT_MENU_ITEM || e.view === 'table');
  const rest = all?.label.replace(/^(усі|все|all)(\s+|$)/iu, '').trim() ?? '';
  if (!rest || Object.values(RECORDS).includes(rest.toLocaleLowerCase())) return typeName;
  return rest.charAt(0).toLocaleUpperCase() + rest.slice(1);
}

/**
 * Параметри статистики для живого лічильника пункту: «Усі» — без параметрів (той самий запит, що й лічильники статусів), пресет і фільтр —
 * власні; null — окремий запит не потрібен (лічильник уже є або його дають фасети: статуси, значення поля, пункт «За …»; пункт режиму
 * бібліотеки лічить лише категорії).
 */
export function countParamsOf(entry: IMenuEntry): IRecordListParams | null {
  if (entry.count !== undefined || entry.mode || /^(status|field|facet):/.test(entry.key)) return null;
  const params: IRecordListParams = {};
  if (entry.preset) params.preset = entry.preset;
  if (entry.filter) params.filter = entry.filter;
  return params;
}
